import React from 'react';

// OKAYLOGO PAGE
export const logoBuilderContent = {
  pageMaxWidth: '1200px',
  breadcrumb: {
    text: 'Back to Home',
    url: '/'
  },

  sections: [
    {
      id: 'hero',
      spacing: 'clamp(32px, 5vw, 64px)',
      blocks: [
        {
          type: 'container',
          narrow: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <span style={{
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #FFA500 100%)',
                  color: 'white',
                  padding: '8px 20px',
                  borderRadius: '25px',
                  fontSize: '13px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  display: 'inline-block'
                }}>
                  Design Tools
                </span>
              )
            }
          ]
        },
        { type: 'spacer', size: 'small' },
        { type: 'title', text: 'OkayLogo', size: 'h1' },
        {
          type: 'image',
          src: '/okaylogo-icon.png',
          alt: 'OkayLogo',
          maxWidth: '100px',
          rounded: true
        },
        {
          type: 'text',
          content: 'Make a logo in 60 seconds.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'text',
          content: 'A free, browser-based logo maker. Pick from 7,000+ icons, customize colors, shapes, gradients, and text — then export as SVG or PNG. No sign-up, no watermarks, no catch.'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
              <a
                href="https://okaylogo.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: 'linear-gradient(135deg, #FF6B6B 0%, #FFA500 100%)',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
              >
                Try OkayLogo
              </a>
              <span style={{
                background: 'rgba(255, 107, 107, 0.1)',
                color: '#FF6B6B',
                padding: '12px 24px',
                borderRadius: '8px',
                fontWeight: '600',
                border: '1px solid rgba(255, 107, 107, 0.2)'
              }}>
                Live
              </span>
            </div>
          )
        }
      ]
    },

    // LIVE EMBED
    {
      id: 'live-demo',
      title: 'Try It Live',
      blocks: [
        {
          type: 'text',
          content: 'Build your logo right here. Pick an icon, customize it, and download — all without leaving the page.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '2px solid var(--border-color)'
            }}>
              <iframe
                src="https://okaylogo.com"
                title="OkayLogo Live"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )
        }
      ]
    },

    // TOOLS
    {
      id: 'tools',
      title: '5 Logo Tools',
      blocks: [
        {
          type: 'custom',
          render: () => {
            const tools = [
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/>
                  </svg>
                ),
                title: 'Logo Builder',
                desc: 'Full customization — icon, colors, gradients, text, shapes, and export'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/>
                  </svg>
                ),
                title: 'Lettermark',
                desc: 'Single-letter logos with custom styling'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/>
                  </svg>
                ),
                title: 'Monogram',
                desc: 'Interlocking initials in a clean layout'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/>
                  </svg>
                ),
                title: 'Badge',
                desc: 'Vintage emblem-style logos'
              },
              {
                icon: (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5 12 2"/>
                  </svg>
                ),
                title: 'Geometric',
                desc: 'Abstract shape-based logos'
              }
            ];
            return (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px'
              }}>
                {tools.map((tool, i) => (
                  <div key={i} style={{
                    background: 'var(--bg-secondary)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid var(--border-color)'
                  }}>
                    <div style={{
                      width: '44px',
                      height: '44px',
                      borderRadius: '12px',
                      background: 'linear-gradient(135deg, rgba(255, 107, 107, 0.12) 0%, rgba(255, 165, 0, 0.12) 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '16px',
                      color: '#FF6B6B'
                    }}>
                      {tool.icon}
                    </div>
                    <h3 style={{ color: 'var(--text-primary)', marginBottom: '6px', fontSize: '1rem', fontWeight: '600' }}>{tool.title}</h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.9rem', margin: 0, lineHeight: '1.5' }}>{tool.desc}</p>
                  </div>
                ))}
              </div>
            );
          }
        }
      ]
    },

    // BUILDER EMBED
    {
      id: 'builder',
      title: 'Logo Builder',
      blocks: [
        {
          type: 'text',
          content: 'Jump straight into the builder — pick an icon, customize, and export.',
          size: 'large'
        },
        { type: 'spacer', size: 'small' },
        {
          type: 'custom',
          render: () => (
            <div style={{
              position: 'relative',
              width: '100%',
              height: '80vh',
              minHeight: '600px',
              background: '#000',
              borderRadius: '20px',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              border: '2px solid var(--border-color)'
            }}>
              <iframe
                src="https://www.okaylogo.com/builder"
                title="OkayLogo Builder"
                style={{
                  width: '100%',
                  height: '100%',
                  border: 'none'
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              />
            </div>
          )
        }
      ]
    },

    // WHAT YOU CAN CUSTOMIZE
    {
      id: 'customization',
      title: 'Full Customization',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'custom',
              render: () => (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '12px'
                }}>
                  {[
                    '7,000+ icons',
                    'Solid & gradient colors',
                    'Circle, square, rounded shapes',
                    'Custom text & font weight',
                    'Letter spacing & gradients',
                    'Border width & color',
                    'Icon sizing & rotation',
                    'Multiple layout options',
                    'SVG & PNG export',
                    'No watermarks'
                  ].map((item, i) => (
                    <div key={i} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FF6B6B" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      <span style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              )
            }
          ]
        }
      ]
    },

    // TECH STACK
    {
      id: 'tech',
      title: 'Tech Stack',
      blocks: [
        {
          type: 'custom',
          render: () => (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: '16px'
            }}>
              {[
                { category: 'Frontend', items: ['React 19', 'Vite', 'Tailwind CSS', 'Framer Motion'] },
                { category: 'State & Icons', items: ['Zustand', 'Lucide React (7,000+ icons)', 'Custom SVG libraries'] },
                { category: 'Export & Rendering', items: ['html2canvas', 'SVG native export', 'No watermarks'] },
                { category: 'Deploy', items: ['Vercel', 'Google Analytics'] }
              ].map((group, i) => (
                <div key={i} style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '12px',
                  padding: '20px'
                }}>
                  <h4 style={{
                    color: '#FF6B6B',
                    fontSize: '0.85rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    marginBottom: '12px'
                  }}>
                    {group.category}
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {group.items.map((item, j) => (
                      <span key={j} style={{
                        color: 'var(--text-secondary)',
                        fontSize: '0.9rem'
                      }}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )
        }
      ]
    },

    // HOW IT WAS BUILT
    {
      id: 'process',
      title: 'How It Was Built',
      blocks: [
        {
          type: 'container',
          background: true,
          blocks: [
            {
              type: 'text',
              content: 'OkayLogo runs entirely in the browser. The logo builder renders everything on a canvas with real-time preview — you see changes as you make them. Icons come from Lucide and custom SVG libraries. Export works by converting the canvas to SVG or using html2canvas for PNG. Zustand handles state so edits feel instant.',
              size: 'normal',
              align: 'left'
            },
            { type: 'spacer', size: 'small' },
            {
              type: 'custom',
              render: () => (
                <ul style={{
                  color: 'var(--text-secondary)',
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  paddingLeft: '20px',
                  margin: 0
                }}>
                  <li>Zustand for lightweight, instant state management</li>
                  <li>Real-time canvas preview with zoom controls</li>
                  <li>5 distinct logo generation tools in one app</li>
                  <li>SVG export for scalable vectors, PNG for raster</li>
                  <li>Dark and light mode with system detection</li>
                  <li>Responsive — works on desktop and mobile</li>
                </ul>
              )
            }
          ]
        }
      ]
    }
  ],

  callToAction: {
    brand: { bg: "#151416", fg: "#FFFFFF", btnBg: "#FFFFFF", btnFg: "#151416" , logo: "/okaylogo-icon.png", logoWidth: 72, logoRadius: 18 },
    title: 'OkayLogo',
    text: 'Make a logo in 60 seconds. Free, no sign-up.',
    button: { text: 'Try OkayLogo →', url: 'https://okaylogo.com' }
  }
};